import React, { useCallback } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import Platform from '@lyearn/core/utils/Platform';
import { useTranslation } from '@lyearn/i18n';
import { Popover, usePopoverContext } from '@lyearn/ui';

import AsyncFilter from '../AsyncFilter';
import StaticFilter from '../StaticFilter';
import { FilterProps } from '../types';
import useFilterListActions from './hooks/useFilterListActions';
import { filterDisabled, keyExtractor } from './utils';

function FilterList(props: FilterProps) {
  const { onClose } = usePopoverContext();
  const { isMobile } = useScreen();
  const {
    value,
    onChange,
    groups,
    itemRenderer,
    overrides,
    isGroupSelectionEnabled,
    asyncOptions,
    defaultPlaceholder,
    showActionButtons = true,
    isSingleSelect = false,
    listPosition = 'bottom-start',
    operators,
    emptyLabel,
    hideSelectedItems,
    classes,
    getTooltipProps,
  } = props;

  const { state, methods } = useFilterListActions({
    selectedItems: value,
    keyExtractor,
    showActionButtons,
    onChange,
    operators,
    selectedOperator: props.selectedOperator,
  });

  const { selectedFilters, selectedOperator } = state;
  const { toggleOption, addMultiple, removeMultiple, removeAll, addSingle, setSelectedOperator } =
    methods;

  const { t } = useTranslation();
  const onApplyFilters = useCallback(() => {
    onChange(selectedFilters, selectedOperator);
    onClose();
  }, [onChange, selectedFilters, selectedOperator, onClose]);

  const clearSelection = useCallback(() => {
    removeAll(filterDisabled);
  }, [removeAll]);

  const actions = React.useMemo(
    () => ({
      addMultiple,
      removeMultiple,
      onApplyFilters,
      clearSelection,
      toggleOption,
      addSingle,
      setOperator: setSelectedOperator,
    }),
    [
      addMultiple,
      removeMultiple,
      onApplyFilters,
      clearSelection,
      toggleOption,
      addSingle,
      setSelectedOperator,
    ],
  );

  const label = defaultPlaceholder
    ? `${t('Common:Filter.FilterBy')} ${defaultPlaceholder}`
    : t('Common:Actions.Filter');

  return (
    <Popover.Content
      bottomSheetProps={{
        enableContentPanningGesture: false,
      }}
      className={Platform.OS === 'web' ? 'w-256' : undefined}
      classes={classes}
      position={listPosition}
      renderAs={isMobile ? 'bottom-sheet' : 'popover'}
      {...overrides}>
      {asyncOptions ? (
        <AsyncFilter
          actions={actions}
          asyncOptions={asyncOptions}
          emptyLabel={emptyLabel}
          filterValue={value}
          getTooltipProps={getTooltipProps}
          groups={groups}
          hideSelectedItems={hideSelectedItems}
          isGroupSelectionEnabled={isGroupSelectionEnabled}
          isSingleSelect={isSingleSelect}
          itemRenderer={itemRenderer}
          label={label}
          operators={operators}
          selectedFilters={selectedFilters}
          selectedOperator={selectedOperator}
          showActionButtons={showActionButtons}
        />
      ) : (
        <StaticFilter
          actions={actions}
          getTooltipProps={getTooltipProps}
          groups={groups}
          isGroupSelectionEnabled={isGroupSelectionEnabled}
          isSingleSelect={isSingleSelect}
          itemRenderer={itemRenderer}
          label={label}
          operators={operators}
          selectedFilters={selectedFilters}
          selectedOperator={selectedOperator}
          showActionButtons={showActionButtons}
        />
      )}
    </Popover.Content>
  );
}

export default FilterList;
